import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import SearchField from 'shared/components/Feedback/FiltersDropdown/SearchField';
import FeedbackSingleSelectFilter from 'shared/components/Feedback/FiltersDropdown/FeedbackSingleSelectFilter';
import { debounce } from 'lodash';
import { noOp } from 'shared/utils/pageUtils';
import { reportFilterKeys, reportQuickFilterKeys } from './utils';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { faLayerGroup, faFilterList } from '@fortawesome/pro-light-svg-icons';
import { FeedbackFilters } from 'shared/components/Feedback/FeedbackDashboardFilterBar';

interface FiltersProps {
  hideQuickFilter?: boolean;
  filterKeys: FeedbackFilters;
  quickFilterKeys?: FeedbackFilters;
  searchPlaceholder: string;
  extraComponent?: JSX.Element;
  handleFilter: (value: typeof reportFilterKeys[number]) => void;
  handleQuickFilter?: React.Dispatch<
    React.SetStateAction<typeof reportQuickFilterKeys[number]>
  >;
  handleSearchText: React.Dispatch<React.SetStateAction<string>>;
}

const StyledDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  // align-items: flex-end;
  gap: ${SPACERS.medium};
  padding-top: ${SPACERS.medium};
  .filter_dropdown__wrapper {
    z-index: 24;
  }
  .filter {
    width: 300px;
    height: 100%;
  }
  .search-field {
    width: 500px;
    height: 48px;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

const ReportFilters: React.FC<FiltersProps> = ({
  filterKeys,
  extraComponent,
  hideQuickFilter = false,
  searchPlaceholder,
  quickFilterKeys = {},
  handleFilter: handleFilters,
  handleQuickFilter: handleQuickFilters,
  handleSearchText,
}) => {
  const [filters, setFilters] = useState<FeedbackFilters>(filterKeys);
  const [quickFilters, setQuickFilters] = useState<FeedbackFilters>(
    quickFilterKeys,
  );

  const handleApplyFilter = useCallback(
    (key: typeof reportFilterKeys[number]) => {
      setFilters(s => {
        const newState = { ...s };
        for (const i of reportFilterKeys) {
          newState[i].selected = i === key;
        }
        return newState;
      });
      handleFilters(key);
    },
    [handleFilters, setFilters],
  );

  const handleApplyQuickFilter = useCallback(
    (key: typeof reportQuickFilterKeys[number]) => {
      if (!handleQuickFilters) return;
      setQuickFilters(s => {
        const newState = { ...s };
        for (const i of reportQuickFilterKeys) {
          newState[i].selected = i === key;
        }
        return newState;
      });
      handleQuickFilters(key);
    },
    [handleQuickFilters, setQuickFilters],
  );

  const handleChange = useCallback(
    debounce((value: string) => {
      handleSearchText(value.length > 2 ? value.trim() : '');
    }, 1300),
    [handleSearchText],
  );

  return (
    <StyledDiv>
      <SearchField
        size="large"
        value={undefined}
        onEnter={handleSearchText}
        onChange={handleChange}
        placeholder={searchPlaceholder}
        className="search-field"
      />
      <div className="filter">
        {/* <div className="heading-label">Group By</div> */}
        <FeedbackSingleSelectFilter
          showClearAll={false}
          feedbackFilter={{
            applyFilter: handleApplyFilter,
            onClear: noOp,
            filters: filters,
          }}
          icon={faLayerGroup}
          defaultValue="Year"
        />
      </div>
      {extraComponent}
      {!hideQuickFilter && (
        <div className="filter quick-filter">
          {/* <div className="heading-label">Quick Filters</div> */}
          <FeedbackSingleSelectFilter
            showClearAll={false}
            feedbackFilter={{
              applyFilter: handleApplyQuickFilter,
              onClear: noOp,
              filters: quickFilters,
            }}
            icon={faFilterList}
            defaultValue="All"
          />
        </div>
      )}
    </StyledDiv>
  );
};

export default React.memo(ReportFilters);
